import { type ScaleLinear } from "d3";
import { debounce } from "lodash-es";
import { equals } from "ramda";
import { PureComponent } from "react";
import { connect } from "react-redux";

import { getExtendedRange, rangeContainsRange, rangeEquals } from "@/app-utils/range.ts";
import {
  geneShapePileupParams,
  gSeqShapePileupParams,
  proteinMappingShapePileupParams,
  transcriptMappingShapePileupParams,
} from "@/app-utils/track-shapes/shapes-settings.ts";
import { addExpressionColor, type IPileup, PileupFactory } from "@/app-utils/track-shapes/track-pileup-factory.ts";
import { getPileupHeight } from "@/app-utils/track-shapes/track-utils.ts";
import { type Dictionary } from "@/app-utils/type.ts";
import {
  type Contig,
  type Dataset,
  type Group,
  type Range,
  type Track,
  type TrackExpressionColoring,
  type TrackFeatures,
} from "@/graphql.ts";
import {
  featureDensityOk,
  fetchTrackCommunityAnnotations,
  fetchTrackExpressionValues,
  fetchTrackFeatures,
  getRangeDensityPerScreenPixel,
  getTrackFeatureTypeCount,
  getTrackId,
  type IGenomeViewerStateLayout,
  type ISortableTrack,
  type ITrackData,
  loadDatasetForTrackExpressionColoring,
  trackCustomizationChanged,
} from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { DEFAULT_TRACK_HEIGHT, TRACK_TYPE } from "@/settings/settings.ts";

import { FeatureTrack } from "../features-track/FeatureTrack.js";
import { type SetTooltipContent } from "../GenomeViewerTracksList.js";

export interface ITrackCommunityAnnotationsData extends ITrackData {
  communityAnnotationsLookup: Dictionary<Group[]>;
  communityAnnotationsOverflow: boolean;
  communityAnnotationsRange: Range;
}

export interface IFeatureTrackData extends TrackFeatures, ITrackData, ITrackCommunityAnnotationsData {
  expressionValuesRange: Range; // query range of expression values (may be larger than visible range due to pre-fetching) ; might be different from the standard features range since there is some threshold for fetching/displaying analytical values
  showAllFeatures: boolean; // flag indicating if all features should be shown, irrespective of max pileup lanes
}

export const INITIAL_FEATURE_TRACK_DATA: Partial<IFeatureTrackData> = {
  features: [],
  expressionValuesRange: null,
  showAllFeatures: false,
  communityAnnotationsLookup: {},
  communityAnnotationsRange: null,
  communityAnnotationsOverflow: false,
};

interface IFeatureTrackLoaderOwnProps {
  track: Track; // track spec
  scale: ScaleLinear<number, number>;
  setTooltipContent: SetTooltipContent;
}

interface IFeatureTrackLoaderAppStateProps {
  trackData: IFeatureTrackData;
  range: Range;
  contig: Contig;
  layout: IGenomeViewerStateLayout;
  showAnnotations: boolean;
}

interface IFeatureTrackLoaderDispatchProps {
  loadDatasetForTrackExpressionColoring: (datasetId: number) => Promise<Dataset>;
  fetchTrackFeatures: (
    track: ISortableTrack,
    contig: Contig,
    range: Range,
    withSequence: boolean,
  ) => Promise<TrackFeatures>;
  fetchTrackExpressionValues: (track: ISortableTrack, contig: Contig, range: Range) => Promise<TrackFeatures>;
  fetchTrackCommunityAnnotations: (track: ISortableTrack, contig: Contig, range: Range) => Promise<any>;
}

interface IFeatureTrackLoaderProps
  extends IFeatureTrackLoaderAppStateProps,
    IFeatureTrackLoaderDispatchProps,
    IFeatureTrackLoaderOwnProps {}

function mapStateToProps(state: IAppState, ownProps: IFeatureTrackLoaderOwnProps): IFeatureTrackLoaderAppStateProps {
  return {
    range: state.genomeBrowser.range,
    contig: state.genomeBrowser.contig,
    trackData: state.genomeBrowser.trackDataMap[getTrackId(ownProps.track)] as IFeatureTrackData,
    layout: state.genomeBrowser.layout,
    showAnnotations: state.auth.session?.params.showCommunityAnnotations,
  };
}

function mapDispatchToProps(dispatch): IFeatureTrackLoaderDispatchProps {
  return {
    loadDatasetForTrackExpressionColoring: (datasetId: number) =>
      dispatch(loadDatasetForTrackExpressionColoring(datasetId)),
    fetchTrackFeatures: (track: ISortableTrack, contig: Contig, range: Range, withSequence: boolean) =>
      dispatch(fetchTrackFeatures(track, contig, range, withSequence)),
    fetchTrackExpressionValues: (track: ISortableTrack, contig: Contig, range: Range) =>
      dispatch(fetchTrackExpressionValues(track, contig, range)),
    fetchTrackCommunityAnnotations: (track: ISortableTrack, contig: Contig, range: Range) =>
      dispatch(fetchTrackCommunityAnnotations(track, contig, range)),
  };
}

interface IFeatureTrackLoaderState {
  pileup: IPileup;
  withSequence: boolean;
  dataOverflow: boolean;
  empty: boolean;
  expressionValuesOverflow: boolean; // indicating if the expression values are not displayed because of density level (used for comparing to fresh computation of the overflow in order to decide whether analytical values should be fetched again independently from the features themselves ; this is another example of what should be moved to local state of Track component...

  // message: string;
  // shouldZoomIn: boolean;
}

class FeatureTrackLoader extends PureComponent<IFeatureTrackLoaderProps, IFeatureTrackLoaderState> {
  private pileupFactory: PileupFactory;

  state = {
    pileup: null,
    withSequence: false,
    dataOverflow: false,
    empty: false,
    expressionValuesOverflow: false,

    // message: null,
    // shouldZoomIn: false,
  };

  loadExpressionDataset = () => {
    const datasetId = this.props.track.trackCustomization?.expressionColoring?.datasetId;
    if (datasetId) {
      this.props.loadDatasetForTrackExpressionColoring(datasetId);
    }
  };

  loadData = () => {
    if (!this.state.dataOverflow) {
      const extendedRange = getExtendedRange(this.props.contig, this.props.range);
      this.props
        .fetchTrackFeatures(this.props.track, this.props.contig, extendedRange, this.state.withSequence)
        // fetching the expression values need to happen AFTER the features have been fetched so that we can more easily merge those objects together (alternative would be to store them separately)
        .then(this.loadExpressionData);
    }
  };

  loadExpressionData = () => {
    const configuredWithExpressionValues = !!this.props.track.trackCustomization?.expressionColoring; // because trackCustomization could refer to empty expressionColoring (e.g. when deleting the customization)

    if (configuredWithExpressionValues && !this.state.expressionValuesOverflow) {
      const extendedRange = getExtendedRange(this.props.contig, this.props.range);
      return this.props.fetchTrackExpressionValues(this.props.track, this.props.contig, extendedRange);
    }
  };

  loadCommunityAnnotationsData = () => {
    if (!this.state.dataOverflow && this.props.showAnnotations) {
      const extendedRange = getExtendedRange(this.props.contig, this.props.range);
      return this.props.fetchTrackCommunityAnnotations(this.props.track, this.props.contig, extendedRange);
    }
  };

  isTrackEmpty = () => {
    const featureTypeCount: number = getTrackFeatureTypeCount(this.props.track, this.props.contig);
    return !featureTypeCount;
  };

  dataOverflow = () => {
    if (
      this.props.track.type === TRACK_TYPE.NUFEATURE ||
      this.props.track.type === TRACK_TYPE.AAFEATURE ||
      this.props.track.type === TRACK_TYPE.MARKER_NUS
    ) {
      return !featureDensityOk(this.props.track, this.props.contig, this.props.range.start, this.props.range.stop);
    }
    // gene, protein, transcript do not have an overflow
    return false;
  };

  expressionValuesOverflow = () => {
    const configuredWithExpressionValues = !!this.props.track.trackCustomization?.expressionColoring; // because trackCustomization could refer to empty expressionColoring (e.g. when deleting the customization)
    return (
      configuredWithExpressionValues &&
      !featureDensityOk(this.props.track, this.props.contig, this.props.range.start, this.props.range.stop)
    );
  };

  debouncedLoadData = debounce(this.loadData, 300, { maxWait: 1000 });
  debouncedLoadCommunityAnnotationsData = debounce(this.loadCommunityAnnotationsData, 300, { maxWait: 1000 });

  componentDidMount() {
    this.pileupFactory = new PileupFactory(this.props.scale);

    this.setState({
      dataOverflow: this.dataOverflow(),
      empty: this.isTrackEmpty(),
      expressionValuesOverflow: this.expressionValuesOverflow(),
      withSequence: this.shouldDownloadProteinSequence(),
    });
    this.loadExpressionDataset();
    this.loadCommunityAnnotationsData();
    this.debouncedLoadData();
  }

  shouldDownloadProteinSequence = (): boolean => {
    if (this.props.track.type !== TRACK_TYPE.PROTEIN_LOCATION) {
      return false;
    }
    const rangeDensity = getRangeDensityPerScreenPixel(this.props.range, this.props.layout.trackWidth);
    return rangeDensity >= 3;
  };

  componentDidUpdate(prevProps: Readonly<IFeatureTrackLoaderProps>, prevState: Readonly<IFeatureTrackLoaderState>) {
    if (this.props.scale !== prevProps.scale) {
      this.pileupFactory = new PileupFactory(this.props.scale);
    }

    // if the expression dataset is loaded or changed, we load this expression dataset's metadata
    const expressionDatasetDiffers =
      this.props.track.trackCustomization?.expressionColoring?.datasetId &&
      this.props.track.trackCustomization?.expressionColoring?.datasetId !==
        prevProps.track.trackCustomization?.expressionColoring?.datasetId;
    if (expressionDatasetDiffers) {
      this.loadExpressionDataset(); // loading of the dataset metadata
    }

    const trackCustomizationDiffers = trackCustomizationChanged(
      this.props.track?.trackCustomization,
      prevProps.track?.trackCustomization,
    );
    const rangeDiffers = !rangeEquals(prevProps.range, this.props.range);
    const contigDiffers: boolean = prevProps.contig?.id !== this.props.contig?.id;
    const boxModelDiffers = !equals(this.props.layout, prevProps.layout);

    // update of flag withSequence (for proteins only)
    if (rangeDiffers || boxModelDiffers) {
      this.setState({ withSequence: this.shouldDownloadProteinSequence() });
    }

    // update of flag expressionValuesOverflow
    if (rangeDiffers || trackCustomizationDiffers) {
      this.setState({ expressionValuesOverflow: this.expressionValuesOverflow() });
    }

    if (rangeDiffers) {
      this.setState({ dataOverflow: this.dataOverflow() });
    }

    if (contigDiffers) {
      this.setState({ empty: this.isTrackEmpty() });
    }

    // if zooming in the range differs but the flag rangeDiffersAndNotIncluded won't be set to true
    const sequenceVisibilityDiffers: boolean = this.state.withSequence !== prevState.withSequence;
    const dataOverflowDiffers: boolean = this.state.dataOverflow !== prevState.dataOverflow;
    const emptyFlagDiffers: boolean = this.state.empty !== prevState.empty;
    const expressionValuesOverflowDiffers: boolean =
      this.state.expressionValuesOverflow !== prevState.expressionValuesOverflow;

    // logic about data fetching
    if (rangeDiffers || contigDiffers || sequenceVisibilityDiffers || dataOverflowDiffers || emptyFlagDiffers) {
      const missingData: boolean =
        !this.props.trackData?.range || !rangeContainsRange(this.props.trackData?.range, this.props.range);
      const missingExpressionData: boolean =
        !this.props.trackData?.expressionValuesRange ||
        !rangeContainsRange(this.props.trackData?.expressionValuesRange, this.props.range);

      if (contigDiffers || sequenceVisibilityDiffers || dataOverflowDiffers) {
        // directly fetch data again
        this.loadData();
      } else if (missingData) {
        // debounced data fetching until range is stabilized
        this.debouncedLoadData();
      } else {
        if (missingExpressionData) {
          // we need to fetch only the expression data corresponding to the existing features (there is no overflow for standard features)
          this.loadExpressionData();
        }
      }
    } else {
      if (expressionValuesOverflowDiffers || trackCustomizationDiffers) {
        // fetch expression data only
        this.loadExpressionData();
      }
    }

    // COMMUNITY ANNOTATIONS REQUESTING
    // no need to check for communityAnnotationsOverflow change since this overflow is driven by MT
    // const communityAnnotationsOverflowDiffers: boolean =
    //  this.props.trackData.communityAnnotationsOverflow !== prevProps.trackData.communityAnnotationsOverflow;
    const showAnnotationsFlagDiffers: boolean = this.props.showAnnotations !== prevProps.showAnnotations;
    if (contigDiffers || dataOverflowDiffers || emptyFlagDiffers || showAnnotationsFlagDiffers) {
      this.loadCommunityAnnotationsData();
    } else if (rangeDiffers) {
      const missingCommunityAnnotationsData: boolean =
        prevProps.trackData?.communityAnnotationsOverflow ||
        !this.props.trackData?.communityAnnotationsRange ||
        !rangeContainsRange(this.props.trackData?.communityAnnotationsRange, this.props.range);
      if (missingCommunityAnnotationsData) {
        this.debouncedLoadCommunityAnnotationsData();
      }
    }

    // LAYOUT UPDATE
    const trackDataDiffers: boolean = this.props.trackData !== prevProps.trackData;
    // update the layout
    if (
      trackDataDiffers ||
      rangeDiffers ||
      expressionValuesOverflowDiffers ||
      dataOverflowDiffers ||
      emptyFlagDiffers ||
      sequenceVisibilityDiffers ||
      trackCustomizationDiffers // useful in case of expression values reset (remove when the expression values are stored independently from the the features)
    ) {
      this.updateLayout();
    } else if (boxModelDiffers) {
      this.debouncedUpdateLayout();
    }
  }

  updateLayout = () => {
    if (this.state.empty || this.state.dataOverflow || this.props.trackData?.features == undefined) {
      return;
    }

    const expressionColoring: TrackExpressionColoring = this.props.track.trackCustomization?.expressionColoring;
    const expressionLaneNumber: number = this.state.expressionValuesOverflow
      ? 0
      : expressionColoring?.conditions?.length || 0;

    const pileup: IPileup = this.pileupFactory.buildFeaturePileup(
      this.props.trackData.features,
      this.props.range,
      this.getPilupParams(),
      this.props.trackData.showAllFeatures,
      expressionLaneNumber,
    );

    if (!this.state.expressionValuesOverflow) {
      addExpressionColor(pileup, expressionColoring);
    }

    this.setState({ pileup: pileup });
  };

  debouncedUpdateLayout = debounce(this.updateLayout, 300, { maxWait: 1000 });

  getPilupParams = () => {
    switch (this.props.track.type) {
      case TRACK_TYPE.GENE:
        return geneShapePileupParams;
      case TRACK_TYPE.TRANSCRIPT_LOCATION:
        return transcriptMappingShapePileupParams;
      case TRACK_TYPE.PROTEIN_LOCATION:
        return proteinMappingShapePileupParams;
      case TRACK_TYPE.NUFEATURE:
      case TRACK_TYPE.AAFEATURE:
      case TRACK_TYPE.MARKER_NUS:
        return gSeqShapePileupParams;
    }
  };

  getTrackHeight = () => {
    let pileupHeight = this.state.dataOverflow ? DEFAULT_TRACK_HEIGHT : getPileupHeight(this.state.pileup);

    // some space for the message informing the user that he should zoom in to see the expression overlays in addition
    if (this.state.expressionValuesOverflow) {
      pileupHeight += 25;
    }

    return pileupHeight;
  };

  render() {
    return (
      <FeatureTrack
        track={this.props.track}
        trackHeight={this.getTrackHeight()}
        pileup={this.state.pileup}
        expressionValuesOverflow={this.state.expressionValuesOverflow}
        overflow={!this.state.empty && this.state.dataOverflow}
        empty={this.state.empty}
        showAllFeatures={this.props.trackData?.showAllFeatures}
        communityAnnotationOverflow={this.props.trackData?.communityAnnotationsOverflow}
        communityAnnotation={this.props.showAnnotations && this.props.trackData?.communityAnnotationsLookup}
        setTooltipContent={this.props.setTooltipContent}
      />
    );
  }
}

const SmartFeatureTrackLoader = connect<
  IFeatureTrackLoaderAppStateProps,
  IFeatureTrackLoaderDispatchProps,
  IFeatureTrackLoaderOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(FeatureTrackLoader);

export { SmartFeatureTrackLoader as FeatureTrackLoader };
